import { getFormattedDisplayNameForEqlField } from '@modules/common/components/EqlFieldsAutoComplete/helpers';
import { QuantifyFormulaDefinition } from '@modules/quantify/components/AggregateFormulaResultTable/QuantifyFormulaDefinition';
import { QuantifyQuery } from '@modules/quantify/store/types';
import { isAggregateFormula } from '@modules/quantify/utils';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { EqlField } from 'generated/graphql';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface getTooltipTitleForFormulaValueCellParams {
  groupBy: QuantifyQuery['groupByField'];
  formulaID?: EQLFunctionName;
  displayName: string;
  feedbackCount: number;
  compareQuery?: QuantifyQuery['compareQuery'];
  isCompareFieldA?: boolean;
  params: GridRenderCellParams;
  measuredAsCriteria: EqlField | undefined;
  shouldRemoveFileUploadPrefix: boolean;
  isUnifiedFieldEnabled: boolean;
}

export const getTooltipTitleForFormulaValueCell = ({
  groupBy,
  formulaID,
  displayName,
  feedbackCount,
  isCompareFieldA,
  compareQuery,
  params,
  measuredAsCriteria,
  shouldRemoveFileUploadPrefix,
  isUnifiedFieldEnabled,
}: getTooltipTitleForFormulaValueCellParams) => {
  if (formulaID === EQLFunctionName.NormaliseByPercent) {
    const compareByLabelSuffix = isCompareFieldA ? 'A' : 'B';
    const compareByLabel = compareQuery?.whereClause
      ? `Filter ${compareByLabelSuffix}`
      : `Duration ${compareByLabelSuffix}`;

    const criteriaDisplayName = measuredAsCriteria
      ? measuredAsCriteria?.displayName
      : 'feedback records';

    return `${
      groupBy
        ? getFormattedDisplayNameForEqlField({
            eqlField: groupBy,
            shouldRemoveFileUploadPrefix,
            isUnifiedFieldEnabled,
          })
        : ''
    } "${displayName}" is present on ${feedbackCount}% of total ${criteriaDisplayName.toLowerCase()} matching ${
      compareQuery ? compareByLabel : 'the query'
    }`;
  }

  if (isAggregateFormula(formulaID)) {
    const getNPS = () => {
      if (compareQuery) {
        return isCompareFieldA
          ? params.row?.nps?.currValue
          : params.row?.nps?.prevValue;
      }

      return params.row?.nps;
    };

    const getCSAT = () => {
      if (compareQuery) {
        return isCompareFieldA
          ? params.row?.csat?.currValue
          : params.row?.csat?.prevValue;
      }

      return params.row?.csat;
    };

    const getMean = () => {
      if (compareQuery) {
        return isCompareFieldA
          ? params.row?.mean?.currValue
          : params.row?.mean?.prevValue;
      }

      return params.row?.mean;
    };

    const getValue = () => {
      if (compareQuery) {
        return isCompareFieldA ? params.row?.currValue : params.row?.prevValue;
      }

      return params.row?.value;
    };

    const nps = getNPS();
    const mean = getMean();
    const csat = getCSAT();

    return (
      <QuantifyFormulaDefinition
        value={getValue()}
        nps={nps}
        csat={csat}
        mean={mean}
      />
    );
  }

  return 'View Feedback';
};
